import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import { IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import { Edit2, Trash } from 'iconsax-react';
import DeleteModal from 'components/modal/DeleteModal';
import SearchInput from 'components/search/SearchInput';
import { nameTagApi } from 'apis/resource/nameTagApis';
import NameTagCreateModal from './NameTagCreateModal';
import NameTagEditModal from './NameTagEditModal';

/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const NameTagTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string>(''); // 검색어
  const [visible, setVisible] = useState({
    create: false,
    edit: false,
    delete: false,
    name: '',
  }); // 모달 show on/off
  const [selectedData, setSelectedData] = useState<any>(null);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await nameTagApi.nameTagList(projectId);
        setData(res.list);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.speaker,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        await nameTagApi.nameTagDelete(
          selectedData.project_id,
          selectedData.speaker,
        );
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: selectedData?.live_name,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, edit: true });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnData = tableData?.map((el, i) => {
    return {
      no: i + 1,
      project_id: el.project_id,
      speaker: el?.speaker,
      main_color: el?.main_color,
      sub_color: el?.sub_color,
      KO: el?.KO,
      EN: el?.EN,
      JA: el?.JA,
      AR: el?.AR,
      ID: el?.ID,
      ES: el?.ES,
      MS: el?.MS,
      RU: el?.RU,
      ZH: el?.ZH,
      SC: el?.SC,
    };
  });

  const columnsOrigin = [
    {
      title: 'No',
      dataIndex: 'no',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: '캐릭터',
      dataIndex: 'speaker',
      width: '4rem',
      align: 'center',
    },
    {
      title: '이름표 바탕색',
      dataIndex: 'main_color',
      width: '8rem',
    },
    {
      title: '이름표 글자색',
      dataIndex: 'sub_color',
      width: '8rem',
    },
    {
      title: '한국어',
      dataIndex: 'KO',
      width: '4rem',
    },
    {
      title: 'English',
      dataIndex: 'EN',
      width: '2rem',
    },
    {
      title: '日本語',
      dataIndex: 'JA',
      width: '4rem',
    },
    {
      title: 'اللغة العربية',
      dataIndex: 'AR',
      width: '4rem',
    },
    {
      dataIndex: '',
      width: '1rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} position='end' />;
      },
    },
  ];

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      const cloneData = JSON.parse(JSON.stringify(data));
      const filterData = cloneData.filter((e) =>
        e.speaker.toLowerCase().includes(searchData.toLowerCase()),
      );
      setTableData(filterData);
    } else {
      setTableData(data);
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder={'Search...'} setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, create: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.nameTagContent.header.button.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable columns={columnsOrigin} dataSource={columnData} />
      </TableWrapper>

      <NameTagCreateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
      />

      <NameTagEditModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        setData={setSelectedData}
        projectId={projectId}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.deleteEmoticonModal.title',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.deleteEmoticonModal.content'
        label={
          selectedData?.image_name
            ? selectedData?.image_name
            : selectedData?.speaker
        }
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default NameTagTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;

const IconArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  p {
    font-size: ${theme.fontSizes.normal} !important;
    font-weight: ${theme.fontWeights.bold} !important;
    line-height: 24px !important;
    letter-spacing: 0.2px !important;
    color: ${theme.colors.blackContent} !important;
  }
`;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
